import React, { CSSProperties } from "react";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-i18next";
import { StyledSelectInput } from "@aureskonnect/react-ui";

import { templateActions, templateStore } from "../templateStore";
import BannerImages from "./BannerImages";

// 100% maps to 960px banner height; width is fixed at 480px. Each percent
// step is 9.6px and the dropdown drives the upload-validation dimensions
// passed to BannerImages via its `size` prop.
export const BANNER_WIDTH = 480;
const HEIGHT_PER_PERCENT = 9.6;
// Phone preview is rendered at half the real scale: 50% maps to 300px in
// the preview frame. Exported so Preview.tsx stays in sync with the form's
// percent → pixel mapping rather than reusing a magic number.
export const PREVIEW_HEIGHT_PER_PERCENT = 6;

type HeightOption = { label: string; value: number };

const HEIGHT_OPTIONS: HeightOption[] = Array.from({ length: 10 }, (_, i) => {
    const value = (i + 1) * 10;
    return { label: `${value}%`, value };
});

export const DEFAULT_BANNER_HEIGHT_PERCENT = 50;

// react-select renders the menu inside the same DOM subtree as the control,
// so siblings/ancestors that establish a stacking context can hide it.
// Boost menu z-index and route it through a body portal so neither
// stacking-context order nor `overflow: hidden` can clip it.
const selectStyles = {
    menu: (base: CSSProperties) => ({ ...base, zIndex: 9999 }),
    menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 9999 }),
};

function readNestedNumber(headPath: string[], snap: any): number {
    const head = headPath.reduce<any>(
        (acc, key) => (acc == null ? acc : acc[key]),
        snap.template
    );
    return typeof head?.BannierHeight === "number"
        ? head.BannierHeight
        : DEFAULT_BANNER_HEIGHT_PERCENT;
}

export function useBannerHeight(
    headPath: string[]
): {
    bannierHeight: number;
    heightPx: number;
    sizeString: (suffix: string) => string;
} {
    const snap = useSnapshot(templateStore);
    const bannierHeight = readNestedNumber(headPath, snap);
    const heightPx = Math.round(bannierHeight * HEIGHT_PER_PERCENT);
    return {
        bannierHeight,
        heightPx,
        sizeString: (suffix) => `${BANNER_WIDTH}*${heightPx} ${suffix}`,
    };
}

interface BannerHeightSelectProps {
    headPath: string[];
    bannierHeight: number;
}

/**
 * Renders the banner-height select bound to the given template path.
 *
 * @remarks `headPath` must be referentially stable across renders
 * (declare it as a module constant, or wrap with `useMemo` at the call
 * site). The `onChange` handler is `useCallback`-memoized on `headPath`,
 * so passing a fresh array every render defeats the memoization.
 */
export function BannerHeightSelect({
    headPath,
    bannierHeight,
}: BannerHeightSelectProps) {
    const handleChange = React.useCallback(
        (option: HeightOption | null) => {
            if (!option) return;
            templateActions.updateTemplate(
                [...headPath, "BannierHeight"],
                option.value
            );
        },
        [headPath]
    );

    return (
        <div style={{ minWidth: "200px" }}>
            <StyledSelectInput
                options={HEIGHT_OPTIONS}
                value={{
                    label: `${bannierHeight}%`,
                    value: bannierHeight,
                }}
                onChange={handleChange}
                isClearable={false}
                isSearchable={false}
                styles={selectStyles}
                menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                }
            />
        </div>
    );
}

interface BannerSectionProps {
    headPath: string[];
}

/**
 * Composes the "Advertising Banner" block (label, height select, image
 * uploader) bound to the given template path.
 *
 * @remarks `headPath` must be referentially stable — same contract as
 * `BannerHeightSelect`. Pass a module constant or memoize at the call
 * site.
 */
export function BannerSection({ headPath }: BannerSectionProps) {
    const { t } = useTranslation();
    const { bannierHeight, sizeString } = useBannerHeight(headPath);
    const imagePath = React.useMemo(
        () => [...headPath, "Bannier"],
        [headPath]
    );

    return (
        <div className="banner-images">
            <h6 className="section-text" style={{ padding: "0 10px" }}>
                {t("Advertising Banner")}
            </h6>
            <div
                className="image-upload-section"
                style={{
                    marginTop: "20px",
                    marginBottom: "20px",
                }}
            >
                <h6 className="section-text">{t("Banner height")}</h6>
                <BannerHeightSelect
                    headPath={headPath}
                    bannierHeight={bannierHeight}
                />
            </div>
            <BannerImages
                dataPath={imagePath}
                title="Image banner"
                size={sizeString("(png,jpg,jpeg ou webp)")}
            />
        </div>
    );
}
